/*
 *   File : holdings.js
 *   Author : https://evoqins.com
 *   Description : holdings component
 *   Integrations : user/holdings
 *   Version : 1.0.0
 */
import React, { useEffect, useRef, useState } from "react";
import { LineChart } from "../../../Components/Charts";
import { Tabs } from "../../../Components/Navigation";
import { Accordion } from "../../../Components/Accordion";
import { DataTableComponent } from "../../../Components/DataTable";
import { APIService } from "../../../Service";
import { Loader } from "../../../Components/Other";

const FILTER_LIST = [
  { value: "3", label: "Last 3 Months" },
  { value: "6", label: "Last 6 Months" },
  { value: "12", label: "Last 1 Year" },
];

const TAB_DATA = [
  { id: 0, label: "All", name: "All" },
  { id: 1, label: "Funds", name: "funds" },
  { id: 2, label: "Goals", name: "Goal" },
];

const COLUMNS = [
  {
    name: "Funds",
    selector: (row) => row.fund_name,
    wrap: true,
    cell: (row) => (
      <div className="d-flex column-gap-2 align-items-center">
        <div className="p-1 bg-white rounded-circle">
          <img
            src={row.image || require("../../../Assets/Images/bank.png")}
            alt={row.fund_name}
            width={16}
          />
        </div>
        <span className="fw-500">{row.fund_name}</span>
      </div>
    ),
  },
  { name: "Asset", selector: (row) => row.asset },
  {
    name: "Allocation",
    selector: (row) => row.allocation,
    cell: (row) => `${row.allocation}%`,
  },
  { name: "Invested", selector: (row) => row.invested_text },
];

export default function Holdings({
  id,
  setFilterData,
  setFilterType,
  appliedFilters,
}) {
  const isRendered = useRef(false);
  const [open, setOpen] = useState(null); // track which accordion is open by investment_id
  const [chartData, setChartData] = useState({});
  const [curTab, setCurTab] = useState(0);
  const [loader, setLoader] = useState(true);
  const [holdingsData, setHoldingsData] = useState(null);

  useEffect(() => {
    setFilterData(FILTER_LIST);
    setFilterType(2);
  }, []);

  // Re-fetch when tab filter (months) or type changes via appliedFilters
  useEffect(() => {
    if (id) _getHoldings();
  }, [id, appliedFilters]);

  /* -----------------------------------
      API SERVICE
  ----------------------------------- */
  const _getHoldings = () => {
    setLoader(true);

    const type = curTab === 1 ? "funds" : curTab === 2 ? "goals" : null;

    APIService(true, "user/holdings", {
      customer_id: id,
      type: type,
      months: appliedFilters?.months ?? null,
    })
      .then((res) => {
        if (res?.status_code === 200) {
          const data = res.data;
          setHoldingsData(data);

          // Map graph_data to chart format
          const graphData = data?.graph_data || [];
          setChartData({
            labels: graphData.map((g) => g.date),
            dataSet: graphData.map((g) => g.value),
          });
          isRendered.current = true;
        }
      })
      .catch((err) => {
        console.error("Holdings error:", err);
      })
      .finally(() => setLoader(false));
  };

  /* -----------------------------------
      HELPERS
  ----------------------------------- */
  const handleTabChange = (tabId) => {
    setCurTab(tabId);
    setOpen(null);
  };

  const _renderSummaryGrid = (summary) => (
    <div className="row row-cols-lg-4 row-cols-md-3 row-cols-2">
      <div className="col">
        <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
          Current Value
        </h4>
        <p className="fw-400 fs-18 lh-24 text-medium-sea-green m-0">
          {summary?.current_value_text ?? "-"}
        </p>
      </div>
      <div className="col">
        <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
          Total Investment
        </h4>
        <p className="fw-400 fs-18 lh-24 text-eerie-black m-0">
          {summary?.total_investment_text ?? "-"}
        </p>
      </div>
      <div className="col">
        <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
          Total Return
        </h4>
        <p className="fw-400 fs-18 lh-24 text-medium-sea-green m-0">
          {summary?.total_return_text ?? "-"}
        </p>
      </div>
      <div className="col">
        <h4 className="fs-14 fw-400 text-davys-gray text-capitalize lh-16">
          1 Day Returns
        </h4>
        <p className="fw-400 fs-18 lh-24 text-medium-sea-green m-0">
          {summary?.one_day_returns_text ?? "-"}
        </p>
      </div>
    </div>
  );

  /* -----------------------------------
      RENDER
  ----------------------------------- */
  return (
    <div className="row">
      <div className="col-12">
        <div className="e-glass-card rounded-16 p-24">
          <div className="pt-24 pb-3">
            <Tabs
              data={TAB_DATA}
              defaultActive={0}
              onChange={handleTabChange}
            />
          </div>

          {loader ? (
            <Loader />
          ) : (
            <>
              <LineChart
                labels={chartData.labels}
                datasets={[
                  {
                    data: chartData.dataSet,
                    borderColor: "#1877F2",
                  },
                ]}
              />
              <p className="fw-400 fs-10 lh-14 text-black pt-1 mb-0 ps-2">
                Showing current values here*
              </p>

              {/* ── ALL TAB ── */}
              {curTab === 0 && (
                <div className="d-flex flex-column w-100 pt-24 rg-24">
                  {/* Overall Summary */}
                  <div className="d-flex flex-column row-gap-3">
                    <h3 className="mb-0 fw-500 fs-28 lh-20 text-eerie-black">
                      Summary
                    </h3>
                    <div className="bg-white-smoke-48 border-white rounded-16 bg-opacity-48 p-3">
                      {_renderSummaryGrid(holdingsData?.summary)}
                    </div>
                  </div>

                  {/* Investments Breakdown */}
                  <div className="d-flex flex-column row-gap-3">
                    <h3 className="mb-0 fw-500 fs-28 lh-20 text-eerie-black">
                      Investments Breakdown
                    </h3>

                    {/* Goals breakdown */}
                    <div className="bg-white-smoke-48 bg-opacity-48 p-3 rounded-16 border-white d-flex flex-column row-gap-3">
                      <h4 className="fw-400 fs-18 lh-24 text-facebook-blue">
                        Goal
                      </h4>
                      {_renderSummaryGrid(
                        holdingsData?.goals_breakdown?.summary,
                      )}
                    </div>

                    {/* Funds breakdown */}
                    <div className="bg-white-smoke-48 bg-opacity-48 p-3 rounded-16 border-white d-flex flex-column row-gap-3">
                      <h4 className="fw-400 fs-18 lh-24 text-facebook-blue">
                        Funds
                      </h4>
                      {_renderSummaryGrid(
                        holdingsData?.funds_breakdown?.summary,
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* ── FUNDS TAB ── */}
              {curTab === 1 && (
                <div className="d-flex flex-column pt-24 row-gap-3">
                  {(holdingsData?.funds_breakdown?.items || []).map((item) => (
                    <Accordion
                      key={item.investment_id}
                      img={item.image || "../../"}
                      title={item.investment_name}
                      isOpen={open === item.investment_id}
                      onClick={() =>
                        setOpen(
                          open === item.investment_id
                            ? null
                            : item.investment_id,
                        )
                      }
                      chipVal={item.type}
                    >
                      <div className="d-flex flex-column p-20">
                        {_renderSummaryGrid(item)}
                      </div>
                      <DataTableComponent
                        info={false}
                        columns={COLUMNS}
                        data={item.funds || []}
                        pagination={false}
                        className="e-table-basic"
                      />
                    </Accordion>
                  ))}
                </div>
              )}

              {/* ── GOALS TAB ── */}
              {curTab === 2 && (
                <div className="d-flex flex-column pt-24 row-gap-3">
                  {(holdingsData?.goals_breakdown?.items || []).map((item) => (
                    <Accordion
                      key={item.investment_id}
                      img={item.image || "../../"}
                      title={item.investment_name}
                      isOpen={open === item.investment_id}
                      onClick={() =>
                        setOpen(
                          open === item.investment_id
                            ? null
                            : item.investment_id,
                        )
                      }
                      chipVal={item.type}
                    >
                      <div className="d-flex flex-column p-20">
                        {_renderSummaryGrid(item)}
                      </div>
                    </Accordion>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
